import React, { useState } from 'react';
import { HiTrash, HiChevronUp, HiChevronDown } from 'react-icons/hi';
import Button from '@components/UI/Button';
import FormSelect from '@components/UI/FormSelect';
import type { EntrySortingRule } from '@einhaus/project314-types';

interface Props {
    rules: EntrySortingRule[];
    onChange: (rules: EntrySortingRule[]) => void;
}

const AVAILABLE_FIELDS = [
    { value: 'predictedProfit', label: 'Predicted Profit (%)', group: 'Predicted Profit' },
    { value: 'avgTradeProfit', label: 'Average Trade Return (%)', group: 'Backtest Metrics' },
    { value: 'medianProfitPercent', label: 'Median Trade Return (%)', group: 'Backtest Metrics' },
    { value: 'medianProfitLongPercent', label: 'Median Long Trade Return (%)', group: 'Backtest Metrics' },
    { value: 'medianProfitShortPercent', label: 'Median Short Trade Return (%)', group: 'Backtest Metrics' },
    { value: 'avgProfitPerDayPercent', label: 'Average Profit Per Day (%)', group: 'Backtest Metrics' },
    { value: 'medianProfitPerDayPercent', label: 'Median Profit Per Day (%)', group: 'Backtest Metrics' },
    { value: 'profitFactor', label: 'Profit Factor', group: 'Backtest Metrics' },
    { value: 'returnOnRiskCapital', label: 'Return on Risk Capital (%)', group: 'Backtest Metrics' },
    { value: 'winRate', label: 'Win Rate', group: 'Backtest Metrics' },
    { value: 'numTrades', label: 'Number of Trades', group: 'Backtest Metrics' },
    { value: 'distinctTradingDays', label: 'Distinct Trading Days', group: 'Backtest Metrics' },
    { value: 'activeMonths', label: 'Active Months', group: 'Backtest Metrics' },
    { value: 'riskAdjustedScore', label: 'Risk-Adjusted Score (Conservative)', group: 'Backtest Metrics' },
    { value: 'profitWeightedScore', label: 'Profit-Weighted Score (Aggressive)', group: 'Backtest Metrics' },
    { value: 'leg1.premiumArorc', label: 'Leg 1: Premium ARORC', group: 'Option Metrics' },
    { value: 'leg1.probabilityOTM', label: 'Leg 1: Probability OTM', group: 'Option Metrics' },
    { value: 'leg1.putSellScore', label: 'Leg 1: Put Sell Score', group: 'Option Metrics' },
    { value: 'leg2.premiumArorc', label: 'Leg 2: Premium ARORC', group: 'Option Metrics' },
    { value: 'leg2.probabilityOTM', label: 'Leg 2: Probability OTM', group: 'Option Metrics' },
    { value: 'leg2.putSellScore', label: 'Leg 2: Put Sell Score', group: 'Option Metrics' }
];

export const EntrySortingRulesEditor: React.FC<Props> = ({ rules, onChange }) => {
    const [showAddForm, setShowAddForm] = useState(false);

    const [newRule, setNewRule] = useState<{
        field: string;
        order: 'asc' | 'desc';
    }>({
        field: '',
        order: 'desc'
    });

    const moveRule = (index: number, direction: 'up' | 'down') => {
        const newRules = [...rules];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        if (targetIndex < 0 || targetIndex >= rules.length) return;

        // Swap rules
        [newRules[index], newRules[targetIndex]] = [newRules[targetIndex], newRules[index]];

        // Update priorities based on new order
        const updatedRules = newRules.map((rule, idx) => ({
            ...rule,
            priority: idx + 1
        }));

        onChange(updatedRules);
    };

    const handleAddRule = () => {
        if (!newRule.field) {
            return;
        }

        const newRuleWithId: EntrySortingRule = {
            id: `rule-${Date.now()}`,
            field: newRule.field,
            order: newRule.order,
            priority: rules.length + 1
        };

        onChange([...rules, newRuleWithId]);

        // Reset form
        setNewRule({ field: '', order: 'desc' });
        setShowAddForm(false);
    };

    const handleRemoveRule = (ruleId: string) => {
        const filteredRules = rules.filter((rule) => rule.id !== ruleId);

        // Update priorities
        const updatedRules = filteredRules.map((rule, index) => ({
            ...rule,
            priority: index + 1
        }));

        onChange(updatedRules);
    };

    const getFieldLabel = (field: string) => {
        const fieldInfo = AVAILABLE_FIELDS.find((f) => f.value === field);
        return fieldInfo?.label || field;
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-zinc-200">Entry Sorting Rules</h3>
                <Button size="sm" variant="outline-dark" onClick={() => setShowAddForm(true)}>
                    + Add Rule
                </Button>
            </div>

            {rules.length === 0 && !showAddForm ? (
                <p className="text-zinc-400">
                    No entry sorting rules configured. Entries will be sorted by average trade profit (descending).
                </p>
            ) : (
                <div className="space-y-2">
                    {rules.map((rule, index) => (
                        <div className="flex items-center space-x-3 p-3 rounded-lg border bg-zinc-800 border-zinc-600" key={rule.id}>
                            <div className="flex flex-col space-y-1">
                                <button
                                    className={`p-1 rounded hover:bg-zinc-700 transition-colors ${
                                        index === 0 ? 'opacity-30 cursor-not-allowed' : ''
                                    }`}
                                    disabled={index === 0}
                                    onClick={() => moveRule(index, 'up')}
                                >
                                    <HiChevronUp className="h-4 w-4 text-zinc-400" />
                                </button>
                                <button
                                    className={`p-1 rounded hover:bg-zinc-700 transition-colors ${
                                        index === rules.length - 1 ? 'opacity-30 cursor-not-allowed' : ''
                                    }`}
                                    disabled={index === rules.length - 1}
                                    onClick={() => moveRule(index, 'down')}
                                >
                                    <HiChevronDown className="h-4 w-4 text-zinc-400" />
                                </button>
                            </div>
                            <div className="flex-1 flex items-center space-x-4">
                                <span className="text-sm text-zinc-400">#{rule.priority}</span>
                                <span className="text-white font-medium">{getFieldLabel(rule.field)}</span>
                                <span
                                    className={`text-sm px-2 py-1 rounded ${
                                        rule.order === 'desc' ? 'bg-green-900 text-green-300' : 'bg-blue-900 text-blue-300'
                                    }`}
                                >
                                    {rule.order === 'desc' ? '↓ Descending' : '↑ Ascending'}
                                </span>
                            </div>
                            <button className="text-red-400 hover:text-red-300 transition-colors" onClick={() => handleRemoveRule(rule.id)}>
                                <HiTrash className="h-5 w-5" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {showAddForm && (
                <div className="p-4 bg-zinc-800 rounded-lg border border-zinc-600 space-y-3">
                    <h4 className="font-medium text-zinc-200">Add Sorting Rule</h4>

                    <div>
                        <label className="block text-sm font-medium text-zinc-200 mb-1" htmlFor="sorting-field-select">
                            Field to Sort By
                        </label>
                        <select
                            className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            id="sorting-field-select"
                            value={newRule.field}
                            onChange={(e) => setNewRule((prev) => ({ ...prev, field: e.target.value }))}
                        >
                            <option value="">Select Field</option>
                            {Object.entries(
                                AVAILABLE_FIELDS.reduce(
                                    (acc, field) => {
                                        if (!acc[field.group]) acc[field.group] = [];
                                        acc[field.group].push(field);
                                        return acc;
                                    },
                                    {} as Record<string, typeof AVAILABLE_FIELDS>
                                )
                            ).map(([group, fields]) => (
                                <optgroup key={group} label={group}>
                                    {fields.map((field) => (
                                        <option disabled={rules.some((r) => r.field === field.value)} key={field.value} value={field.value}>
                                            {field.label} {rules.some((r) => r.field === field.value) && '(already added)'}
                                        </option>
                                    ))}
                                </optgroup>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-zinc-200 mb-1" htmlFor="sorting-order-select">
                            Sort Order
                        </label>
                        <FormSelect
                            id="sorting-order-select"
                            options={[
                                { value: 'desc', label: 'Descending (High to Low)' },
                                { value: 'asc', label: 'Ascending (Low to High)' }
                            ]}
                            value={newRule.order}
                            onChange={(e) => setNewRule((prev) => ({ ...prev, order: e.target.value as 'asc' | 'desc' }))}
                        />
                    </div>

                    <div className="flex space-x-2">
                        <Button disabled={!newRule.field} size="sm" variant="primary" onClick={handleAddRule}>
                            Add Rule
                        </Button>
                        <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => {
                                setShowAddForm(false);
                                setNewRule({ field: '', order: 'desc' });
                            }}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            )}

            {rules.length > 0 && (
                <div className="mt-4 p-3 bg-zinc-900 rounded-lg">
                    <p className="text-sm text-zinc-400">
                        <strong>How it works:</strong> When multiple entries occur at the same timestamp, they will be sorted by these rules
                        in priority order. Higher priority rules (lower numbers) take precedence over lower priority rules.
                    </p>
                </div>
            )}
        </div>
    );
};
